import type { PlayableItemDto } from '@mimir/common/dto/playableItemDto';
import type { PlaybackQuality, QualityOption } from '@mimir/common/types/playbackQuality';
import { playableArtifactToQuality } from '@mimir/common/util/playableArtifactToQuality';

export interface QualitySelectionResult {
  success: boolean;
  quality: PlaybackQuality | null;
  error?: string;
}

export interface QualityChangeState {
  inProgress: boolean;
  error?: string;
}

export interface QualityState {
  availableQualities: QualityOption[];
  currentQuality: PlaybackQuality | null;
  requestedQuality: PlaybackQuality | null;
  qualityChangeInProgress: boolean;
}

/**
 * Get a user-friendly label for a playback quality.
 * If both segmented and flat versions exist for the same quality type then use the format as suffix.
 */
export function getQualityLabel(quality: PlaybackQuality, availableQualities: PlaybackQuality[]): string {
  const baseLabels: Record<PlaybackQuality, string> = {
    'original-segmented': 'Original quality',
    'original-flat': 'Original quality',
    'edit-proxy-segmented': 'Edit proxy',
    'edit-proxy-flat': 'Edit proxy',
    'web-proxy-segmented': 'Web proxy',
    'web-proxy-flat': 'Web proxy',
  };

  let label = baseLabels[quality];

  if (quality.includes('edit-proxy-')) {
    const hasBoth = availableQualities.includes('edit-proxy-segmented') && availableQualities.includes('edit-proxy-flat');
    if (hasBoth) {
      label += quality.endsWith('-segmented') ? ' (TS)' : ' (MP4)';
    }
  } else if (quality.includes('web-proxy-')) {
    const hasBoth = availableQualities.includes('web-proxy-segmented') && availableQualities.includes('web-proxy-flat');
    if (hasBoth) {
      label += quality.endsWith('-segmented') ? ' (TS)' : ' (MP4)';
    }
  }

  return label;
}

/**
 * Get the icon file name for a playback quality (without extension).
 * Used with MIcon component.
 */
export function getQualityIconPath(quality: PlaybackQuality): string {
  const iconFiles: Record<PlaybackQuality, string> = {
    'original-segmented': 'video_quality_original_seg',
    'original-flat': 'video_quality_original',
    'edit-proxy-segmented': 'video_quality_edit_seg',
    'edit-proxy-flat': 'video_quality_edit',
    'web-proxy-segmented': 'video_quality_web_seg',
    'web-proxy-flat': 'video_quality_web',
  };
  return iconFiles[quality];
}

export function getAvailableQualitiesFromItem(payload: PlayableItemDto, failedQualities: Map<PlaybackQuality, string> | null): QualityOption[] {
  const options: QualityOption[] = [];
  const availableQualities: PlaybackQuality[] = payload.availableArtifacts.map(entry => playableArtifactToQuality(entry)).filter((q): q is PlaybackQuality => q !== null);

  for (const artifactEntry of payload.availableArtifacts) {
    const quality = playableArtifactToQuality(artifactEntry);
    if (quality) {
      const failedReason = failedQualities?.get(quality);
      const disabledText = artifactEntry.playableStatus === 'unplayable' ? artifactEntry.reason : undefined;
      const warningText = artifactEntry.playableStatus === 'playable' ? artifactEntry.warning : undefined;
      options.push({
        quality,
        label: getQualityLabel(quality, availableQualities),
        icon: getQualityIconPath(quality),
        disabledText: failedReason ?? disabledText,
        warningText,
      });
    }
  }
  return options;
}

/**
 * In-memory storage for the user's last selected playback quality.
 * Resets on page refresh.
 */
let lastSelectedQuality: PlaybackQuality | null = null;

export function saveLastSelectedQuality(quality: PlaybackQuality): void {
  lastSelectedQuality = quality;
}

export function getLastSelectedQuality(): PlaybackQuality | null {
  return lastSelectedQuality;
}

/**
 * Select quality to use when loading an item.
 * Returns the user's last selected quality if it's available and playable,
 * otherwise returns the default quality from the item's playable artifact.
 */
export function selectQualityForLoad(
  availableQualities: QualityOption[],
  itemDefaultQuality: PlaybackQuality | null,
  userLastSelectedQuality: PlaybackQuality | null
): PlaybackQuality | null {
  if (availableQualities.length === 0) {
    return itemDefaultQuality;
  }

  if (userLastSelectedQuality) {
    const lastSelectedOption = availableQualities.find(
      option => option.quality === userLastSelectedQuality && !option.disabledText
    );

    if (lastSelectedOption) {
      return userLastSelectedQuality;
    }
  }

  return itemDefaultQuality;
}
